import React, {
    type FC,
    type PropsWithChildren,
    type ReactNode,
    memo,
    use,
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { Button, cn, Modal, useOverlayState } from "@heroui/react";
import { AnimatePresence, motion, stagger, type Transition, type Variants } from "framer-motion";

import logo from "../assets/NU-Logo.png";
import { AppRoutes } from "@domain/routing/routeConstants";
import { AppSettingsContext } from "@domain/context/AppSettingsProvider";
import { CompanyAccountingYearContext, CompanyContext } from "@domain/context/CompanyContext";
import { GQL } from "@binale-tech/shared";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { ProductDetails, ProductGroups, type ProductKey, ProductKeys } from "@domain/models/Product";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { UserContext } from "@domain/context/UserProvider";
import "./HomeView.css";
import { HomeMetricsWidget } from "@app/pages/home/HomeMetricsWidget";
import { HomeCalendarWidget } from "@app/pages/home/HomeCalendarWidget";
import { HomeOpenPositionsWidget } from "@app/pages/home/HomeOpenPositionsWidget";
import { HomeAiUsageWidget } from "@app/pages/home/HomeAiUsageWidget";
import { BinaleAgentButton } from "@app/pages/home/agent/BinaleAgentButton";
import { Books, Calendar, Lock } from "@gravity-ui/icons";
import dayjs from "dayjs";

interface ComponentButtonProps {
    link: string;
    product: ProductKey;
    icon?: ReactNode;
}

const calendarIcon = <Calendar className="size-4" />;

const item = {
    hidden: { y: 50, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
    },
};
const ComponentButton: FC<PropsWithChildren<ComponentButtonProps>> = (props) => {
    const { companyGQL } = use(CompanyContext);
    const { isReady } = use(ReadinessContext);
    const navigate = useNavigate();
    const user = use(UserContext);
    const disabled = !isReady || !ProductAccessUtils.canRead(props.product, companyGQL, user);
    const handlePress = useCallback(() => navigate(props.link), [navigate, props.link]);

    return (
        <motion.div variants={item}>
            <Button
                size="lg"
                variant="ghost"
                className="HomeView_button bg-app-surface/50 backdrop-blur-md border border-app-surface/80 shadow-lg"
                isDisabled={disabled}
                onPress={handlePress}
            >
                {props.icon}
                {props.children}
            </Button>
        </motion.div>
    );
};
const container: Variants = {
    hidden: { opacity: 1, scale: 0 },
    visible: { opacity: 1, scale: 1 },
};

const containerTransition: Transition = {
    delayChildren: stagger(0.05, { startDelay: 0.3 }),
};

const LockOverlay = () => {
    return (
        <div className="fixed top-[50px] inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-app-surface/80 backdrop-blur-md p-8 rounded-lg shadow-xl text-center">
                <Lock className="mx-auto mb-4 text-muted size-16" />
                <h2 className="text-2xl font-bold mb-2">
                    <FormattedMessage id="app.page.home.temporarilyLocked" />
                </h2>
            </div>
        </div>
    );
};

const Clock: FC = () => {
    // Tick every second so `dayjs()` re-reads and the seconds key bumps. Cheaper
    // than storing the whole Date in state; we only need the component to rerender.
    const [, tick] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            tick((t) => t + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const onMouseEnter = useCallback(() => setIsHovered(true), []);
    const onMouseLeave = useCallback(() => setIsHovered(false), []);

    const now = dayjs();
    const seconds = now.format("ss");
    const showSeparator = now.second() % 2 === 0;
    const separatorStyle = useMemo(() => ({ opacity: showSeparator ? 1 : 0 }), [showSeparator]);

    return (
        <span
            className="block text-left p-px text-zinc-900 [text-shadow:0_0_2px_rgb(255_255_255/0.9),0_0_12px_rgb(255_255_255/0.75)]"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <span className="flex items-end font-mono ">
                <div className="block text-[6rem] leading-normal text-right">
                    {now.format("HH")}
                    <span className="transition-opacity duration-800 ease-in-out" style={separatorStyle}>
                        :
                    </span>
                    {now.format("mm")}
                </div>
                {/* Seconds mount only on hover - avoids running an invisible
                    per-tick framer-motion animation the rest of the time. */}
                {isHovered ? (
                    <span
                        className={cn(
                            "relative inline-block overflow-hidden align-top mb-4 ml-2 w-[2ch] h-16 text-[2rem]",
                            "leading-[2rem] tabular-nums",
                        )}
                    >
                        <AnimatePresence>
                            <motion.span
                                key={seconds}
                                // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- framer-motion AnimatePresence exit animation; stabilizing risks the keyed enter/exit transition
                                initial={{ y: "-100%", opacity: 0 }}
                                // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- framer-motion AnimatePresence exit animation; stabilizing risks the keyed enter/exit transition
                                animate={{ y: 0, opacity: 1 }}
                                // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- framer-motion AnimatePresence exit animation; stabilizing risks the keyed enter/exit transition
                                exit={{ y: "100%", opacity: 0 }}
                                // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- framer-motion AnimatePresence exit animation; stabilizing risks the keyed enter/exit transition
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                {seconds}
                            </motion.span>
                        </AnimatePresence>
                    </span>
                ) : (
                    <span className="relative w-[41px]"></span>
                )}
            </span>
            <span className="block text-left text-[2rem] font-bold capitalize">{now.format("dddd, DD.MM.YYYY")}</span>
        </span>
    );
};

const HomeViewImpl = () => {
    const { formatMessage } = useIntl();
    const buchModalState = useOverlayState();
    const isModalOpen = useDeferredValue(buchModalState.isOpen);
    const { companyGQL } = use(CompanyContext);
    const userData = use(UserContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const { useClassicImage } = use(AppSettingsContext);

    const onModalOpenChange = useCallback(
        (open: boolean) => (open ? buchModalState.open() : buchModalState.close()),
        [buchModalState],
    );

    // The accounting overview widgets are for whoever owns the company's numbers:
    // the company owner as well as platform admins (same owner check as
    // CompaniesView's `canEdit`).
    const canSeeCompanyOverview =
        Boolean(userData.isAdmin) ||
        (Boolean(companyGQL?.owner?.id) && companyGQL?.owner?.id === userData.fireUser?.uid);
    //
    // useEffect(() => {
    //     };
    //     const cacheKey = dayjs().format("YYYY-MM-DD");
    //         return;
    //     }
    //     }).then(([res]) => {
    //     });
    // }, []);

    //     }
    //     return data;

    return (
        <div>
            <div
                className={cn("HomeView__bg", {
                    "HomeView__bg--autumn0": dayjs().week() % 2 === 0,
                    "HomeView__bg--autumn1": dayjs().week() % 2 === 1,

                    // "HomeView__bg--summer0": dayjs().week() % 2 === 0,
                    // "HomeView__bg--summer1": dayjs().week() % 2 === 1,

                    // "HomeView__bg--snow0": dayjs().week() % 5 === 0,
                    // "HomeView__bg--snow1": dayjs().week() % 5 === 1,
                    // "HomeView__bg--snow2": dayjs().week() % 5 === 2,
                    // "HomeView__bg--snow3": dayjs().week() % 5 === 3,
                    // "HomeView__bg--snow4": dayjs().week() % 5 === 4,
                    // "HomeView__bg--snow5": dayjs().week() % 5 === 5,
                    "HomeView__bg--classic": useClassicImage,
                })}
            ></div>
            <div className="HomeView__container">
                <motion.div variants={container} initial="hidden" animate="visible" transition={containerTransition}>
                    <ComponentButton
                        link={ProductDetails.details[ProductKeys.DMS].link}
                        product={ProductKeys.DMS}
                        icon={ProductDetails.details[ProductKeys.DMS].icon}
                    >
                        <FormattedMessage id="app.titles.DMS" tagName="span" />
                    </ComponentButton>

                    <motion.div variants={item}>
                        <Button
                            size="lg"
                            variant="ghost"
                            className="HomeView_button bg-app-surface/50 backdrop-blur-md border border-app-surface/80 shadow-lg"
                            onPress={buchModalState.open}
                            isDisabled={!ProductAccessUtils.hasUserAccounting(companyGQL, userData)}
                        >
                            <Books className="size-4" />
                            <FormattedMessage id="app.titles.accounting" tagName="span" />
                        </Button>
                    </motion.div>

                    <ComponentButton
                        link={ProductDetails.details[ProductKeys.Banks].link}
                        product={ProductKeys.Banks}
                        icon={ProductDetails.details[ProductKeys.Banks].icon}
                    >
                        <FormattedMessage id="app.titles.Banks" tagName="span" />
                    </ComponentButton>

                    <ComponentButton
                        link={ProductDetails.details[ProductKeys.CashRegister].link}
                        product={ProductKeys.CashRegister}
                        icon={ProductDetails.details[ProductKeys.CashRegister].icon}
                    >
                        <FormattedMessage id="app.titles.CashRegister" tagName="span" />
                    </ComponentButton>

                    <ComponentButton
                        link={ProductDetails.details[ProductKeys.Invoices].link}
                        product={ProductKeys.Invoices}
                        icon={ProductDetails.details[ProductKeys.Invoices].icon}
                    >
                        <FormattedMessage id="app.titles.Invoices" tagName="span" />
                    </ComponentButton>

                    <ComponentButton
                        link={ProductDetails.details[ProductKeys.AVM].link}
                        product={ProductKeys.AVM}
                        icon={ProductDetails.details[ProductKeys.AVM].icon}
                    >
                        <FormattedMessage id={"app.titles." + ProductDetails.details[ProductKeys.AVM].pk} />
                    </ComponentButton>

                    <ComponentButton
                        link={ProductDetails.details[ProductKeys.Contacts].link}
                        product={ProductKeys.Contacts}
                        icon={ProductDetails.details[ProductKeys.Contacts].icon}
                    >
                        <FormattedMessage id={"app.titles." + ProductDetails.details[ProductKeys.Contacts].pk} />
                    </ComponentButton>

                    <ComponentButton
                        link={AppRoutes.calendar}
                        product={ProductKeys.AccountingCommon}
                        icon={calendarIcon}
                    >
                        <FormattedMessage id="app.titles.calendar" tagName="span" />
                    </ComponentButton>

                    <ComponentButton
                        link={ProductDetails.details[ProductKeys.OCR].link}
                        product={ProductKeys.OCR}
                        icon={ProductDetails.details[ProductKeys.OCR].icon}
                    >
                        <FormattedMessage id={"app.titles." + ProductDetails.details[ProductKeys.OCR].pk} />
                    </ComponentButton>
                </motion.div>
                <div className="grow" />
                {/* Two widget columns. The AI column sits left of the accounting
                    ones and is bottom-aligned, since it has no Clock above it to
                    anchor against. Both HomeWidgetShell stacks expand as
                    right-anchored overlays, so neither column reflows the other. */}
                <div className="flex flex-col">
                    <div className="flex justify-end">
                        <Clock />
                    </div>
                    <div className="flex justify-end pb-3">
                        <BinaleAgentButton />
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="flex flex-col items-start gap-4">
                            <HomeAiUsageWidget />
                        </div>
                        <div className="flex flex-col items-start gap-4">
                            {canSeeCompanyOverview && (
                                <>
                                    <HomeMetricsWidget />
                                    <HomeOpenPositionsWidget />
                                    <HomeCalendarWidget />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {companyGQL?.id === "berlin_nu_airport_com__a26f22fc-a34b-4e87-b773-b9b3146ab4e8" ? (
                <img src={logo} alt="logotype" className="w-32 absolute bottom-3.5 right-2.5" />
            ) : companyGQL?.logo ? (
                <img src={companyGQL.logo} alt="logotype" width={200} className="absolute bottom-3.5 right-2.5 w-32" />
            ) : null}
            <div className="HomeView__bottomLine w-full flex justify-between">
                {/* <div className="flex gap-2 text-sm opacity-50"> */}
                {/* </div> */}
                <div>
                    <Link to={AppRoutes.impressum}>
                        <FormattedMessage id="app.page.legal.impressum" />
                    </Link>
                    <Link to={AppRoutes.haftungsausschluss}>
                        <FormattedMessage id="app.page.legal.haftungsausschluss" />
                    </Link>
                    <Link to={AppRoutes.agb}>
                        <FormattedMessage id="app.page.legal.agb" />
                    </Link>
                    <Link to={AppRoutes.datenschutz}>
                        <FormattedMessage id="app.page.legal.datenschutz" />
                    </Link>
                    <Link to={AppRoutes.cookies}>
                        <FormattedMessage id="app.page.legal.cookiePolicy" />
                    </Link>
                    <span>{import.meta.env.VITE_VERSION || "local"}</span>
                </div>
            </div>
            {companyGQL?.isLocked && <LockOverlay />}
            <Modal.Backdrop variant="transparent" isOpen={isModalOpen} onOpenChange={onModalOpenChange}>
                <Modal.Container>
                    <Modal.Dialog
                        aria-label="Buchungen"
                        className="w-250 max-w-250 bg-app-surface/10 backdrop-blur-md border border-app-surface/40"
                    >
                        <Modal.CloseTrigger />
                        <Modal.Body className="bg-app-surface/0 overflow-hidden">
                            <motion.div
                                className="flex justify-center items-start"
                                variants={container}
                                initial="hidden"
                                animate="visible"
                                transition={containerTransition}
                            >
                                <div>
                                    {ProductDetails.getGroup(ProductGroups.Buch).map((d) => (
                                        <ComponentButton key={d.pk} link={d.link} product={d.pk}>
                                            {d.icon}
                                            <FormattedMessage id={"app.titles." + d.pk} />
                                        </ComponentButton>
                                    ))}
                                </div>
                                <div>
                                    <ComponentButton
                                        link={AppRoutes.accountsView}
                                        product={ProductKeys.AccountingCommon}
                                    >
                                        <FormattedMessage id="app.titles.KA" tagName="span" />
                                    </ComponentButton>
                                    <ComponentButton link={AppRoutes.SuSa} product={ProductKeys.AccountingCommon}>
                                        <FormattedMessage id="app.titles.SuSa" tagName="span" />
                                    </ComponentButton>
                                    <ComponentButton link={AppRoutes.BWA} product={ProductKeys.AccountingCommon}>
                                        BWA
                                    </ComponentButton>
                                    {yearConfig?.taxation !== GQL.ICompanyTaxation.Kust && (
                                        <ComponentButton link={AppRoutes.UStVA} product={ProductKeys.AccountingCommon}>
                                            <FormattedMessage id="app.titles.Vat" tagName="span" />
                                        </ComponentButton>
                                    )}
                                    <ComponentButton
                                        link={AppRoutes.recordImportExport}
                                        product={ProductKeys.AccountingCommon}
                                    >
                                        <FormattedMessage id="app.titles.import_export" tagName="span" />
                                    </ComponentButton>
                                    <ComponentButton
                                        link={AppRoutes.filtersAndSearchView}
                                        product={ProductKeys.AccountingCommon}
                                    >
                                        <FormattedMessage id="app.titles.filtern" tagName="span" />
                                    </ComponentButton>
                                    <ComponentButton
                                        link={AppRoutes.manageAccount}
                                        product={ProductKeys.AccountingCommon}
                                    >
                                        <FormattedMessage id="app.titles.kontenverwaltung" tagName="span" />
                                    </ComponentButton>
                                </div>
                            </motion.div>
                        </Modal.Body>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </div>
    );
};

export const HomeView = memo(HomeViewImpl);
